'use client';

import { useState } from 'react';

import type { realestatelst } from '@ps/models/lib/realestate-listing-page';
import { cn } from '@ps/ui/utils';
import { ChevronDown } from 'lucide-react';

import { getFeatureType } from './feature-types';

// 5 rows on the 2-column desktop layout.
const INITIAL_VISIBLE_COUNT = 10;

// Fades the bottom of the grid when collapsed so users see there's more below.
// Last ~25% of the grid height progressively masks to ~30% opacity (subtle).
const FADE_MASK_CLASSES =
  '[mask-image:linear-gradient(to_bottom,black_75%,rgba(0,0,0,0.3)_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_75%,rgba(0,0,0,0.3)_100%)]';

interface FeaturesGridProps {
  features: realestatelst.ListingFeatureItem[];
}

/**
 * Grid of feature tiles with a "Show all N features" / "Show less" toggle
 * when the list exceeds the initial visible count. Used by both the editor
 * preview and the public view so the truncation behavior stays in sync.
 */
export function FeaturesGrid({ features }: FeaturesGridProps) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = features.length > INITIAL_VISIBLE_COUNT;
  const visible =
    expanded || !hasMore ? features : features.slice(0, INITIAL_VISIBLE_COUNT);

  const showFadeMask = hasMore && !expanded;

  return (
    <div>
      <div
        className={cn(
          'grid grid-cols-1 gap-3 sm:grid-cols-2',
          showFadeMask && FADE_MASK_CLASSES,
        )}
      >
        {visible.map(feature => {
          const featureType = getFeatureType(feature.typeId);
          const Icon = featureType?.icon;
          return (
            <div
              key={feature.id}
              className="flex items-center gap-3 rounded-xl bg-gray-200 p-4 dark:bg-gray-700"
            >
              {Icon && (
                <Icon className="h-5 w-5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
              )}
              <span className="line-clamp-2 text-sm text-gray-700 dark:text-gray-300">
                {feature.label}
              </span>
            </div>
          );
        })}
      </div>
      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded(prev => !prev)}
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 underline-offset-2 hover:underline dark:text-gray-300"
          >
            {expanded ? 'Show less' : `Show all ${features.length} features`}
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                expanded && 'rotate-180',
              )}
            />
          </button>
        </div>
      )}
    </div>
  );
}
